import React, { useState, useEffect } from 'react';
import { User as UserIcon, Mail, Shield, Edit2, Save, X, Phone, Building } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../../store';
import { selectUser } from '../../store/slices/authSlice';
import { updateUser } from '../../store/slices/usersSlice';
import Button from '../ui/Button';
import './ProfilePage.css';

const ProfilePage: React.FC = () => {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(selectUser);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [formData, setFormData] = useState({
    id: '',
    email: '',
    firstName: '',
    lastName: '',
    role: '',
    phoneNumber: '',
    companyId: ''
  });

  useEffect(() => {
    if (currentUser) {
      setFormData({
        id: currentUser.id,
        email: currentUser.email,
        firstName: currentUser.firstName,
        lastName: currentUser.lastName,
        role: currentUser.role,
        phoneNumber: '', // User type doesn't have it, but AdminProfile does. 
        companyId: currentUser.companyName || ''
      });
    }
  }, [currentUser]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    if (!currentUser) return;

    setIsSaving(true);
    try {
      await dispatch(updateUser({
        id: currentUser.id,
        userData: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phoneNumber: formData.phoneNumber
        }
      })).unwrap();
      setIsEditing(false);
    } catch (err: any) {
      alert(err || 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (currentUser) {
      setFormData({
        id: currentUser.id,
        email: currentUser.email,
        firstName: currentUser.firstName,
        lastName: currentUser.lastName,
        role: currentUser.role,
        phoneNumber: '',
        companyId: currentUser.companyName || ''
      });
    }
    setIsEditing(false);
  };

  return (
    <div className="profile-page">
      <div className="profile-header">
        <div className="profile-title">
          <h1>Profile Settings</h1>
        </div>
        <div className="header-actions">
          {!isEditing ? (
            <Button
              variant="primary"
              className="edit-btn"
              onClick={() => setIsEditing(true)}
            >
              <Edit2 size={16} />
              Edit Profile
            </Button>
          ) : (
            <div className="edit-actions">
              <Button
                variant="secondary"
                className="cancel-btn"
                onClick={handleCancel}
              >
                <X size={16} />
                Cancel
              </Button>
              <Button
                variant="primary"
                className="save-btn"
                onClick={handleSave}
                loading={isSaving}
                disabled={isSaving}
              >
                <Save size={16} />
                Save Changes
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="profile-content">
        <div className="user-information-section">
          <div className="section-header">
            <h2>User Information</h2>
          </div>

          <div className="user-info-form">
            <div className="form-group">
              <label htmlFor="email">
                <Mail size={16} />
                Email
              </label>
              {isEditing ? (
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="form-input"
                />
              ) : (
                <div className="form-value">{formData.email}</div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="firstName">
                <UserIcon size={16} />
                First Name
              </label>
              {isEditing ? (
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className="form-input"
                />
              ) : (
                <div className="form-value">{formData.firstName}</div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="lastName">
                <UserIcon size={16} />
                Last Name
              </label>
              {isEditing ? (
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className="form-input"
                />
              ) : (
                <div className="form-value">{formData.lastName}</div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="phoneNumber">
                <Phone size={16} />
                Phone Number
              </label>
              {isEditing ? (
                <input
                  type="tel"
                  id="phoneNumber"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  className="form-input"
                />
              ) : (
                <div className="form-value">{formData.phoneNumber || 'N/A'}</div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="role">
                <Shield size={16} />
                Role
              </label>
              <div className="form-value">
                <span className={`role-badge ${formData.role}`}>
                  {formData.role}
                </span>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="company">
                <Building size={16} />
                Company/Org
              </label>
              <div className="form-value">{formData.companyId || 'N/A'}</div>
            </div>
          </div>
        </div>

        <div className="profile-info-card">
          <div className="profile-avatar">
            <div className="avatar-circle">
              <UserIcon size={32} />
            </div>
          </div>
          <div className="profile-details">
            <h3>{formData.firstName} {formData.lastName}</h3>
            <p className="profile-email">{formData.email}</p>
            <p className="profile-role">
              <span className={`role-badge ${formData.role}`}>{formData.role}</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
